import { themeConWeb } from '@app/util/modernThemeConweb'
import {
  Box,
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  TextField,
  Typography,
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { ExpandMore, Warning } from '@material-ui/icons'
import React from 'react'
import { Field } from 'redux-form'

import { maxSelect, minSelect, valueRequired } from '../../util/validators'
import { LOCAL_STORAGE_ERROR } from '../../util/webStorageUtils'
import MenuCheckboxAnswers from './MenuCheckboxAnswers'
import MenuRadioAnswers from './MenuRadioAnswers'

export const formName = 'personalize'
export const questionPrefix = 'q_'

const useStyles = makeStyles(theme => ({
  expansionSummaryContent: {
    marginBottom: '0 !important',
  },
  warningIcon: {
    color: themeConWeb.color.dangerLight,
    display: props => (props.error && props.touched ? 'inline' : 'none'),
    fontSize: '18px',
    marginLeft: '8px',
  },
}))

const PersonalizeOrderForm = ({
  answers,
  displayedQuestions, // The post personalized price of the current in-checkout cart
  handleSubmit,
  onCheckboxChange,
  onRadioChange,
  onSubmit,
  personalizeFormError,
}) => {
  const classes = useStyles()
  return (
    <form>
      {displayedQuestions.map(function(question) {
        const firstLevelAnswers = question.answers.map(a => answers[a])

        let isRadio = question.minimumSelection === 1 && question.maximumSelection === 1
        // is expanded the selection is required has an optional amount up to or is sub menu
        let isExpanded =
          question.minimumSelection > 0 || question.maximumSelection > 1 || question.depth >= 2

        return (
          <ExpansionPanelElevationZero
            defaultExpanded={isExpanded}
            elevation={0}
            // If the item is required or is a bunch of options items open the expansion panel
            key={question.id}
            square
          >
            <ExpansionPanelSummary
              classes={{ expanded: classes.expansionSummaryContent }}
              expandIcon={<ExpandMore color="primary" />}
              id="panel1a-header"
            >
              <Box alignItems="center" display="flex" flexDirection="row">
                <Typography color="textPrimary" component="h2" variant="subtitle1">
                  {question.text}

                  {/* If a single selection is required */}
                  {question.minimumSelection === 1 && <> (Required)</>}

                  {/* If an item is not required and you can pick up to X */}
                  {question.maximumSelection > 1 && !question.minimumSelection >= 1 && (
                    <> (Choose up to {question.maximumSelection})</>
                  )}

                  {/* if more than one is required */}
                  {question.maximumSelection > 1 && question.minimumSelection >= 1 && (
                    <> (Required Choose up to {question.maximumSelection})</>
                  )}
                </Typography>

                {/* Render the error with a same `name` Field as the input, in order to access `error` and `touched`  */}
                <Field component={renderError} name={`${questionPrefix}${question.id}`} />
              </Box>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              {question.description && <p>{question.description}</p>}
              {isRadio ? (
                <Field
                  answers={firstLevelAnswers}
                  component={MenuRadioAnswers}
                  name={`${questionPrefix}${question.id}`}
                  normalize={value => Number(value)}
                  onAnswerChange={onRadioChange}
                  question={question}
                  questionId={question.id}
                  validate={question.minimumSelection === 1 ? valueRequired : null}
                />
              ) : (
                <Field
                  answers={firstLevelAnswers}
                  component={MenuCheckboxAnswers}
                  name={`${questionPrefix}${question.id}`}
                  onAnswerChange={onCheckboxChange}
                  validate={[
                    maxSelect(question.maximumSelection),
                    minSelect(question.minimumSelection),
                  ]}
                />
              )}
            </ExpansionPanelDetails>
          </ExpansionPanelElevationZero>
        )
      })}
      <Box marginTop={4}>
        <Field
          aria-label="special instructions"
          color="secondary"
          component={renderTextField}
          fullWidth={true}
          id="specialInstructions"
          label="Special Instructions"
          multiline
          name="specialInstructions"
          placeholder="e.g. Dressing on the side"
          rows="5"
          rowsMax="5"
          variant="outlined"
        />
      </Box>
      <button
        id="add-to-checkout"
        onClick={handleSubmit(values => onSubmit(values, false))}
        style={{ visibility: 'hidden' }}
        type="submit"
      ></button>
      <button
        id="move-to-checkout"
        onClick={handleSubmit(values => onSubmit(values, true))}
        style={{ visibility: 'hidden' }}
        type="submit"
      ></button>
      <div className="row middle-xs">
        <div className="col-xs-12 center-xs">
          {/* @jacob Looks like you touched this last. I don't believe this is doing anything can you take a look? */}
          {personalizeFormError && (
            <Typography color="error" variant="body1">
              {personalizeFormError.message.includes('QuotaExceeded')
                ? LOCAL_STORAGE_ERROR
                : personalizeFormError.message}
            </Typography>
          )}
        </div>
      </div>
    </form>
  )
}

/**
 * https://redux-form.com/7.3.0/examples/material-ui/
 * Needed to create a wrapper component for redux forms check docs for details
 */
const renderTextField = ({ input, label, meta: { error, touched }, ...custom }) => (
  <TextField label={label} {...input} {...custom} />
)

const renderError = ({ meta: { error, touched } }) => {
  const classes = useStyles({ error, touched }) // eslint-disable-line react-hooks/rules-of-hooks
  return <Warning className={classes.warningIcon} />
}

/**
 *  We do not have plans to reuse this component at this time. If we want to overwrite the base
 *  work work with UX if you would like to repeat this pattern else ware.
 */
const ExpansionPanelElevationZero = withStyles({
  expanded: {},
  /* Styles applied to the root element if `expanded={true}`. */
  root: {
    '&$disabled': {},
    '&$expanded': {
      '&:before': {
        opacity: 1,
      },
      margin: 0,
    },
    '&$expanded + &': {
      '&:before': {
        display: 'block',
      },
    },
    '&:first-child': {
      '&:before': {
        display: 'none',
      },
    },
    position: 'relative',
  },
})(ExpansionPanel)

export default PersonalizeOrderForm
